/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        OpenAPI 3.1 DTO Schemas for BYOK (Bring Your Own Key)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

export const SetByokKeySchema = z
  .object({
    provider: z.enum(['openai', 'anthropic', 'google-ai-studio', 'deepseek']).openapi({
      description: 'AI Provider identifier code',
      example: 'openai',
    }),
    apiKey: z.string().min(5).max(500).openapi({
      description: 'Raw private API key from the AI provider to be AES-GCM encrypted',
      example: 'sk-proj-abc123456789xyz',
    }),
  })
  .openapi({
    title: 'SetByokKeyRequest',
    description: 'Request payload to configure or update a workspace BYOK key',
  });

export type SetByokKeyDto = z.infer<typeof SetByokKeySchema>;

export const ByokKeyItemSchema = z
  .object({
    provider: z.string().openapi({
      description: 'AI Provider identifier',
      example: 'openai',
    }),
    maskedKey: z.string().openapi({
      description: 'Masked API key for safe UI inspection',
      example: 'sk-p...9xyz',
    }),
    updatedAt: z.number().openapi({
      description: 'Timestamp when this key was updated (epoch milliseconds)',
      example: 1718000000000,
    }),
  })
  .openapi({
    title: 'ByokKeyItem',
    description: 'Configured BYOK entry for a workspace',
  });

export type ByokKeyItemDto = z.infer<typeof ByokKeyItemSchema>;

export const ByokListResponseSchema = z.array(ByokKeyItemSchema).openapi({
  title: 'ByokListResponse',
  description: 'List of all active BYOK keys configured in the workspace',
});

export const SetByokSuccessSchema = z
  .object({
    provider: z.string().openapi({ example: 'openai' }),
    maskedKey: z.string().openapi({ example: 'sk-p...9xyz' }),
    message: z.string().openapi({ example: 'API key successfully encrypted and saved' }),
  })
  .openapi({
    title: 'SetByokSuccessResponse',
    description: 'Confirmation payload following successful BYOK encryption and storage',
  });
